/**
 * Wishlist UI layer: wires up heart buttons (collection cards + product page) and,
 * on the dedicated wishlist page, renders the empty/populated states.
 *
 * Depends on: LalumeWishlistService (wishlist-service.js), which depends on
 * LalumeWishlistStorage (wishlist-storage.js). Both must load before this file.
 */
(function () {
  'use strict';

  var service = window.LalumeWishlistService;
  if (!service) return;

  /* ------------------------------------------------------------------ *
   * Heart buttons (collection cards + product page)
   * ------------------------------------------------------------------ */

  function syncHeartState(button) {
    var productId = button.getAttribute('data-product-id');
    var variantId = button.getAttribute('data-variant-id');
    var active = service.has(productId, variantId);
    button.setAttribute('data-wishlisted', active ? 'true' : 'false');
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function initHeartButtons(root) {
    var buttons = (root || document).querySelectorAll('[data-wishlist-heart]');
    buttons.forEach(function (button) {
      if (button.__wishlistBound) {
        syncHeartState(button);
        return;
      }
      button.__wishlistBound = true;
      syncHeartState(button);
      button.addEventListener('click', function (evt) {
        evt.preventDefault();
        evt.stopPropagation();
        var productId = button.getAttribute('data-product-id');
        var variantId = button.getAttribute('data-variant-id');
        var handle = button.getAttribute('data-product-handle');
        service.toggle(productId, variantId, handle);
      });
    });
  }

  document.addEventListener('wishlist:updated', function () {
    initHeartButtons(document);
  });

  /**
   * The PDP heart is rendered server-side for the variant selected on page load. If the
   * shopper then switches material via the theme's variant picker, keep the heart's
   * data-variant-id in sync so wishlisting always captures the variant actually on screen.
   */
  if (window.jQuery) {
    window.jQuery(document).on('variantChanged', '.product-form', function (evt, variant) {
      if (!variant) return;
      var heart = evt.currentTarget.querySelector('.wishlist-heart--pdp');
      if (!heart) return;
      heart.setAttribute('data-variant-id', variant.id);
      syncHeartState(heart);
    });
  }

  /* ------------------------------------------------------------------ *
   * Wishlist page
   * ------------------------------------------------------------------ */

  var pageRoot = document.querySelector('[data-wishlist-page]');

  function moneyFormat(cents) {
    var format =
      (window.theme && (window.theme.money_format_with_product_code_preference || window.theme.money_format)) ||
      '${{amount}}';
    if (window.theme && window.theme.Shopify && window.theme.Shopify.formatMoney) {
      return window.theme.Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  if (pageRoot) {
    var config = window.LalumeWishlistPageConfig || {};
    var swatchOptionNames = (config.swatchOptionNames || []).map(function (n) {
      return n.trim().toLowerCase();
    });
    var swatchEnabled = !!config.swatchEnabled;

    var titleEl = pageRoot.querySelector('[data-wishlist-title]');
    var emptyEl = pageRoot.querySelector('[data-wishlist-empty]');
    var itemsEl = pageRoot.querySelector('[data-wishlist-items]');
    var removeAllBtn = pageRoot.querySelector('[data-wishlist-remove-all]');

    var productCache = {};
    var renderToken = 0;

    // /products/{handle}.js never includes metafields, so it can't see the
    // Image Gallery (custom.image_gallery) variant metafield - it's fetched
    // separately from the product.wishlist-data alternate template and merged
    // in as variant.galleryImage. A failure there just means the wishlist
    // falls back to the standard Shopify image, same as before this existed.
    function fetchGalleryImages(handle) {
      return fetch('/products/' + handle + '?view=wishlist-data')
        .then(function (res) {
          if (!res.ok) throw new Error('gallery data fetch failed');
          return res.json();
        })
        .then(function (data) {
          var byId = {};
          (data.variants || []).forEach(function (v) {
            if (v.galleryImage) byId[v.id] = v.galleryImage;
          });
          // hidden = variants with no image of their own (snippets/variant-image-gate.liquid)
          return { gallery: byId, hidden: data.hidden || [] };
        })
        .catch(function () {
          return { gallery: {}, hidden: [] };
        });
    }

    function fetchProduct(handle) {
      if (productCache[handle]) return Promise.resolve(productCache[handle]);
      return Promise.all([
        fetch('/products/' + handle + '.js').then(function (res) {
          if (!res.ok) throw new Error('product fetch failed');
          return res.json();
        }),
        fetchGalleryImages(handle)
      ])
        .then(function (results) {
          var data = results[0];
          var galleryImagesByVariantId = results[1].gallery;
          var hiddenVariantIds = results[1].hidden.map(String);
          data.variants.forEach(function (v) {
            var galleryImage = galleryImagesByVariantId[v.id];
            if (galleryImage) v.galleryImage = galleryImage;
          });
          // Variants without an image never appear as a choice: drop them before any row/swatch/dropdown is built.
          // A wishlist entry saved on one of them falls back to the product's first visible variant (renderRow).
          if (hiddenVariantIds.length) {
            data.variants = data.variants.filter(function (v) {
              return hiddenVariantIds.indexOf(String(v.id)) === -1;
            });
          }
          productCache[handle] = data;
          return data;
        })
        .catch(function () {
          return null;
        });
    }

    // Same detection rule as the PDP letter dropdown (snippets/variant-picker.liquid):
    // matches only "Letter"/"Initial" (case-insensitive), never a swatch/Material option.
    function isLetterOption(name) {
      var n = String(name || '').toLowerCase().trim();
      return n === 'letter' || n === 'initial';
    }

    // /products/{handle}.js has returned options as an array of {name, position, values}
    // objects on this store; handle that shape but fall back to plain strings defensively,
    // since that's what Shopify's docs describe for this endpoint.
    function optionName(product, index) {
      var opt = product.options[index];
      return typeof opt === 'string' ? opt : opt && opt.name;
    }

    function findSwatchOption(product) {
      if (!swatchEnabled || !product.options) return -1;
      for (var i = 0; i < product.options.length; i++) {
        var name = optionName(product, i);
        if (name && swatchOptionNames.indexOf(String(name).toLowerCase()) !== -1) {
          return i;
        }
      }
      return -1;
    }

    // Same idea as findSwatchOption, for the Letter/Initial option (Step 2).
    function findLetterOption(product) {
      if (!product.options) return -1;
      for (var i = 0; i < product.options.length; i++) {
        if (isLetterOption(optionName(product, i))) return i;
      }
      return -1;
    }

    function titleCase(str) {
      return String(str)
        .toLowerCase()
        .replace(/(^|\s)\S/g, function (c) {
          return c.toUpperCase();
        });
    }

    // Some options (e.g. "LETTER" on a GOLD-only product listing) bundle a word that's
    // constant across every value ("A GOLD", "B GOLD", ...) purely because the option
    // wasn't split out separately. Strip whatever word(s) are common to every value in
    // the option — derived from the product's own data, never a hardcoded material name
    // — so the wishlist shows "Letter: A" instead of the redundant "Letter: A Gold".
    function stripRedundantSuffix(value, allValues) {
      if (!allValues || allValues.length < 2) return value;
      var tokenized = allValues.map(function (v) {
        return String(v).trim().split(/\s+/);
      });
      var minLen = Math.min.apply(
        null,
        tokenized.map(function (t) {
          return t.length;
        })
      );
      var commonSuffixLen = 0;
      for (var i = 1; i <= minLen - 1; i++) {
        var word = tokenized[0][tokenized[0].length - i];
        var allMatch = tokenized.every(function (t) {
          return t[t.length - i] === word;
        });
        if (allMatch) {
          commonSuffixLen = i;
        } else {
          break;
        }
      }
      if (commonSuffixLen === 0) return value;
      var tokens = String(value).trim().split(/\s+/);
      if (tokens.length <= commonSuffixLen) return value;
      return tokens.slice(0, tokens.length - commonSuffixLen).join(' ');
    }

    /**
     * Material + Letter/Initial (Step 2). Only used when a product has BOTH a
     * swatch/Material option and a Letter/Initial option - everything else
     * (Material-only, Letter-only, any other option) goes through the
     * pre-existing branches in renderVariantInfo, completely untouched.
     *
     * Derives everything from this product's own variants/options - never
     * hardcodes a material or letter value, an option name beyond the same
     * "letter"/"initial" match already used by the PDP, or an option position.
     *
     * Reuses the exact same resolution rule as the PDP's material+letter
     * dependency (assets/theme.js _.initMaterialLetterDependency): switching
     * Material keeps the current Letter if that combination exists, otherwise
     * falls back to the first Letter (in the option's own canonical order)
     * that does exist for the newly selected Material. Switching Letter
     * always stays within the currently selected Material, since the Letter
     * dropdown is only ever built from that material's own variants.
     *
     * Availability is deliberately NOT checked when deciding which letters
     * appear - only existence is. A real-but-sold-out variant (e.g. Silver/F)
     * stays selectable; only combinations with no matching variant at all are
     * excluded. Sold-out state itself is handled where it already was: the
     * row's Add to Cart control is re-derived from whatever variant ends up
     * stored, on the existing full re-render that service.changeVariant()
     * already triggers.
     */
    function renderMaterialAndLetter(product, variant, swatchIndex, letterIndex) {
      var materialKey = 'option' + (swatchIndex + 1);
      var letterKey = 'option' + (letterIndex + 1);
      var letterOptionDef = product.options[letterIndex];
      var letterName = optionName(product, letterIndex);
      var canonicalLetters = (letterOptionDef && letterOptionDef.values) || [];

      var currentMaterial = variant[materialKey];
      var currentLetter = variant[letterKey];

      var variantById = {};
      var materialsSeen = [];
      var materialData = {};
      product.variants.forEach(function (v) {
        variantById[v.id] = v;
        var m = v[materialKey];
        var l = v[letterKey];
        if (!m || !l) return;
        if (!materialData[m]) {
          materialData[m] = {};
          materialsSeen.push(m);
        }
        if (materialData[m][l] === undefined) materialData[m][l] = v.id;
      });

      function pickLetterFor(material) {
        var lettersForMaterial = materialData[material];
        if (!lettersForMaterial) return null;
        if (currentLetter && lettersForMaterial[currentLetter] !== undefined) {
          return currentLetter;
        }
        for (var i = 0; i < canonicalLetters.length; i++) {
          if (lettersForMaterial[canonicalLetters[i]] !== undefined) {
            return canonicalLetters[i];
          }
        }
        var fallbackKeys = Object.keys(lettersForMaterial);
        return fallbackKeys.length ? fallbackKeys[0] : null;
      }

      var swatchButtons = '';
      materialsSeen.forEach(function (m) {
        var targetLetter = pickLetterFor(m);
        var targetVariantId = targetLetter ? materialData[m][targetLetter] : null;
        var isCurrent = m === currentMaterial;
        var targetVariant = targetVariantId != null ? variantById[targetVariantId] : null;
        swatchButtons +=
          '<button type="button" class="wishlist-swatch' +
          (isCurrent ? ' wishlist-swatch--active' : '') +
          '" data-wishlist-swatch data-variant-id="' +
          (targetVariantId != null ? targetVariantId : '') +
          '" data-swatch="' +
          escapeHtml(String(m).toLowerCase()) +
          '" aria-label="' +
          escapeHtml(titleCase(m)) +
          '" aria-current="' +
          (isCurrent ? 'true' : 'false') +
          '"' +
          (targetVariant && !targetVariant.available ? ' data-unavailable="true"' : '') +
          '></button>';
      });

      var lettersForCurrentMaterial = materialData[currentMaterial] || {};
      var letterOptionsHtml = '';
      canonicalLetters.forEach(function (letterVal) {
        var vId = lettersForCurrentMaterial[letterVal];
        if (vId === undefined) return; // no variant for this Material+Letter - excluded, not just disabled
        var isCurrent = letterVal === currentLetter;
        var display = stripRedundantSuffix(letterVal, canonicalLetters);
        letterOptionsHtml +=
          '<option value="' +
          vId +
          '"' +
          (isCurrent ? ' selected' : '') +
          '>' +
          escapeHtml(titleCase(display)) +
          '</option>';
      });

      return (
        '<div class="wishlist-item__variant">' +
        '<span class="wishlist-item__variant-label">Material: ' +
        escapeHtml(titleCase(currentMaterial || '')) +
        '</span>' +
        '<div class="wishlist-item__swatches">' +
        swatchButtons +
        '</div>' +
        '</div>' +
        '<div class="wishlist-item__variant">' +
        '<span class="wishlist-item__variant-label">' +
        escapeHtml(titleCase(letterName)) +
        ':</span>' +
        '<select class="wishlist-letter-select" data-wishlist-letter-select aria-label="' +
        escapeHtml(titleCase(letterName)) +
        '">' +
        letterOptionsHtml +
        '</select>' +
        '</div>'
      );
    }

    // Swatch-eligible option (matches settings.swatch_option_name, e.g. Colour/Variations)
    // is labelled the friendly "Material" — those internal names aren't customer-facing
    // vocabulary here. Any other real option keeps its actual Shopify option name.
    function renderVariantInfo(product, variant, swatchIndex) {
      var letterIndex = findLetterOption(product);
      if (swatchIndex !== -1 && letterIndex !== -1) {
        return renderMaterialAndLetter(product, variant, swatchIndex, letterIndex);
      }

      if (swatchIndex !== -1) {
        var optionKey = 'option' + (swatchIndex + 1);
        var seen = {};
        var swatchButtons = '';
        product.variants.forEach(function (v) {
          var value = v[optionKey];
          if (!value || seen[value]) return;
          seen[value] = true;
          var isCurrent = String(v.id) === String(variant.id);
          swatchButtons +=
            '<button type="button" class="wishlist-swatch' +
            (isCurrent ? ' wishlist-swatch--active' : '') +
            '" data-wishlist-swatch data-variant-id="' +
            v.id +
            '" data-swatch="' +
            escapeHtml(String(value).toLowerCase()) +
            '" aria-label="' +
            escapeHtml(titleCase(value)) +
            '" aria-current="' +
            (isCurrent ? 'true' : 'false') +
            '"' +
            (v.available ? '' : ' data-unavailable="true"') +
            '></button>';
        });
        return (
          '<div class="wishlist-item__variant">' +
          '<span class="wishlist-item__variant-label">Material: ' +
          escapeHtml(titleCase(variant[optionKey] || '')) +
          '</span>' +
          '<div class="wishlist-item__swatches">' +
          swatchButtons +
          '</div>' +
          '</div>'
        );
      }

      for (var i = 0; i < product.options.length; i++) {
        var name = optionName(product, i);
        if (!name || name.toLowerCase() === 'title') continue;
        var optKey = 'option' + (i + 1);
        var rawValue = variant[optKey];
        if (!rawValue) continue;
        var opt = product.options[i];

        if (isLetterOption(name)) {
          var seenLetter = {};
          var letterOptionsHtml = '';
          product.variants.forEach(function (v) {
            var val = v[optKey];
            if (!val || seenLetter[val]) return;
            seenLetter[val] = true;
            var isCurrent = String(v.id) === String(variant.id);
            var display = stripRedundantSuffix(val, opt && opt.values);
            letterOptionsHtml +=
              '<option value="' +
              v.id +
              '"' +
              (isCurrent ? ' selected' : '') +
              '>' +
              escapeHtml(titleCase(display)) +
              '</option>';
          });
          return (
            '<div class="wishlist-item__variant">' +
            '<span class="wishlist-item__variant-label">' +
            escapeHtml(titleCase(name)) +
            ':</span>' +
            '<select class="wishlist-letter-select" data-wishlist-letter-select aria-label="' +
            escapeHtml(titleCase(name)) +
            '">' +
            letterOptionsHtml +
            '</select>' +
            '</div>'
          );
        }

        var displayValue = stripRedundantSuffix(rawValue, opt && opt.values);
        return (
          '<div class="wishlist-item__variant-label">' +
          escapeHtml(titleCase(name)) +
          ': ' +
          escapeHtml(titleCase(displayValue)) +
          '</div>'
        );
      }

      return '';
    }

    function variantImage(product, variant) {
      if (variant && variant.galleryImage) return variant.galleryImage;
      if (variant && variant.featured_image && variant.featured_image.src) {
        return variant.featured_image.src;
      }
      if (product.featured_image) return product.featured_image;
      if (product.images && product.images.length) return product.images[0];
      return null;
    }

    function buildImgUrl(src, size) {
      if (!src) return '';
      return src.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, '_' + size + '$1$2');
    }

    function addToCart(variantId, quantity, button) {
      button.disabled = true;
      var originalText = button.textContent;
      button.textContent = 'Adding…';

      var body = new URLSearchParams({
        id: variantId,
        quantity: quantity,
        sections: 'cart-drawer'
      });

      fetch(window.theme && window.theme.routes ? window.theme.routes.cart_add_url : '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json'
        },
        body: body.toString()
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            button.textContent = 'Unavailable';
            setTimeout(function () {
              button.disabled = false;
              button.textContent = originalText;
            }, 2000);
            return;
          }

          if (window.theme && typeof window.theme.addedToCartHandler === 'function' && window.jQuery) {
            window.theme.addedToCartHandler.call(window.jQuery(button.closest('.wishlist-item')), result.data);
          } else {
            document.documentElement.dispatchEvent(
              new CustomEvent('theme:cartchanged', {
                bubbles: true,
                cancelable: false,
                detail: { openDrawer: true }
              })
            );
          }

          button.textContent = 'Added';
          setTimeout(function () {
            button.disabled = false;
            button.textContent = originalText;
          }, 1500);
        })
        .catch(function () {
          button.disabled = false;
          button.textContent = originalText;
        });
    }

    function renderRow(item, product) {
      var variant = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === String(item.variantId)) {
          variant = product.variants[i];
          break;
        }
      }
      if (!variant) variant = product.variants[0];

      var img = variantImage(product, variant);
      var swatchIndex = findSwatchOption(product);
      var quantity = item.quantity || 1;
      var available = !!variant.available;

      var row = document.createElement('div');
      row.className = 'wishlist-item';
      row.setAttribute('data-wishlist-item', '');
      row.setAttribute('data-product-id', item.productId);
      row.setAttribute('data-variant-id', item.variantId);
      row.setAttribute('data-handle', item.handle);

      var productUrl = '/products/' + product.handle + '?variant=' + variant.id;

      var imageHtml =
        '<a class="wishlist-item__image" href="' + productUrl + '" tabindex="-1" aria-hidden="true">' +
        (img
          ? '<img src="' + buildImgUrl(img, '300x300') + '" alt="" loading="lazy" width="150" height="150">'
          : '') +
        '</a>';

      var variantInfoHtml = renderVariantInfo(product, variant, swatchIndex);

      var cartControl = available
        ? '<button type="button" class="button wishlist-item__add-to-cart" data-wishlist-add-to-cart>ADD TO CART</button>'
        : '<button type="button" class="button wishlist-item__add-to-cart wishlist-item__add-to-cart--sold-out" disabled aria-disabled="true">SOLD OUT</button>';

      row.innerHTML =
        imageHtml +
        '<div class="wishlist-item__info">' +
        '<a class="wishlist-item__title" href="' +
        productUrl +
        '">' +
        escapeHtml(product.title) +
        '</a>' +
        '<div class="wishlist-item__price">' +
        moneyFormat(variant.price) +
        '</div>' +
        variantInfoHtml +
        '</div>' +
        '<div class="wishlist-item__qty" role="group" aria-label="Quantity">' +
        '<button type="button" class="wishlist-qty__btn" data-wishlist-qty-decrease aria-label="Decrease quantity">' +
        '−</button>' +
        '<input type="number" class="wishlist-qty__input" min="1" step="1" value="' +
        quantity +
        '" data-wishlist-qty-input aria-label="Quantity" inputmode="numeric">' +
        '<button type="button" class="wishlist-qty__btn" data-wishlist-qty-increase aria-label="Increase quantity">+</button>' +
        '</div>' +
        '<div class="wishlist-item__cart">' +
        cartControl +
        '</div>' +
        '<button type="button" class="wishlist-item__remove" data-wishlist-remove aria-label="Remove from wishlist">' +
        '<span class="wishlist-item__remove-symbol" aria-hidden="true">×</span>' +
        '<span class="wishlist-item__remove-text" aria-hidden="true">Remove</span>' +
        '</button>';

      row.querySelector('[data-wishlist-remove]').addEventListener('click', function () {
        service.remove(item.productId, item.variantId);
      });

      row.querySelector('[data-wishlist-qty-decrease]').addEventListener('click', function () {
        var input = row.querySelector('[data-wishlist-qty-input]');
        var next = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
        input.value = next;
        service.setQuantity(item.productId, item.variantId, next);
      });

      row.querySelector('[data-wishlist-qty-increase]').addEventListener('click', function () {
        var input = row.querySelector('[data-wishlist-qty-input]');
        var next = Math.max(1, (parseInt(input.value, 10) || 1) + 1);
        input.value = next;
        service.setQuantity(item.productId, item.variantId, next);
      });

      row.querySelector('[data-wishlist-qty-input]').addEventListener('change', function (evt) {
        var next = Math.max(1, parseInt(evt.target.value, 10) || 1);
        evt.target.value = next;
        service.setQuantity(item.productId, item.variantId, next);
      });

      if (available) {
        row.querySelector('[data-wishlist-add-to-cart]').addEventListener('click', function (evt) {
          addToCart(variant.id, quantity, evt.currentTarget);
        });
      }

      row.querySelectorAll('[data-wishlist-swatch]').forEach(function (swatchBtn) {
        swatchBtn.addEventListener('click', function () {
          var newVariantId = swatchBtn.getAttribute('data-variant-id');
          service.changeVariant(item.productId, item.variantId, newVariantId);
        });
      });

      var letterSelect = row.querySelector('[data-wishlist-letter-select]');
      if (letterSelect) {
        letterSelect.addEventListener('change', function (evt) {
          service.changeVariant(item.productId, item.variantId, evt.target.value);
        });
      }

      return row;
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = String(str == null ? '' : str);
      return div.innerHTML;
    }

    function renderWishlistPage() {
      var myToken = ++renderToken;
      var items = service.getAll();

      if (!items.length) {
        titleEl.textContent = 'MY WISHLIST';
        emptyEl.hidden = false;
        itemsEl.hidden = true;
        itemsEl.innerHTML = '';
        if (removeAllBtn) removeAllBtn.hidden = true;
        return;
      }

      titleEl.textContent = 'MY WISHLIST (' + items.length + ')';
      emptyEl.hidden = true;
      if (removeAllBtn) removeAllBtn.hidden = false;

      var handles = [];
      items.forEach(function (item) {
        if (handles.indexOf(item.handle) === -1) handles.push(item.handle);
      });

      Promise.all(handles.map(fetchProduct)).then(function () {
        if (myToken !== renderToken) return; // a newer render superseded this one

        itemsEl.innerHTML = '';
        itemsEl.hidden = false;

        items.forEach(function (item) {
          var product = productCache[item.handle];
          if (!product) return; // product removed/unavailable; skip rather than crash
          itemsEl.appendChild(renderRow(item, product));
        });
      });
    }

    if (removeAllBtn) {
      removeAllBtn.addEventListener('click', function () {
        service.removeAll();
      });
    }

    document.addEventListener('wishlist:updated', renderWishlistPage);
    renderWishlistPage();
  }

  /* ------------------------------------------------------------------ *
   * Init
   * ------------------------------------------------------------------ */

  initHeartButtons(document);
})();
